// DAAM Admin — Kling 2.6 Pro pilot (fal.ai text-to-video)
// Admin-only. Feature-flag gated server-side. Reads /config first;
// disables Generate when the server says pro_gate style disabled.
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, Info, XCircle, Loader2, Download, PlayCircle } from "lucide-react";
import { api } from "../../lib/api";

const API_BASE     = process.env.REACT_APP_BACKEND_URL || "";
const PENDING_KEY  = "daam_kling_pending_job";
const IDEMP_KEY    = "daam_kling_pending_idemp";
const POLL_MS      = 3000;
const TERMINAL     = ["ready", "failed"];

function uuid() {
  return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) => {
    const v = (Math.random() * 16) | 0; return (c ^ v).toString(16);
  });
}

export default function KlingPilotPage() {
  const [config, setConfig]         = useState(null);
  const [configErr, setConfigErr]   = useState(null);

  const [prompt, setPrompt]                   = useState("");
  const [durationSeconds, setDurationSeconds] = useState(10);
  const [aspectRatio, setAspectRatio]         = useState("9:16");
  const [generateAudio, setGenerateAudio]     = useState(false);

  const [confirmPrompt, setConfirmPrompt] = useState(false);
  const [confirmCharge, setConfirmCharge] = useState(false);

  const [estimate, setEstimate]     = useState(null);
  const [job, setJob]               = useState(null);
  const [error, setError]           = useState(null);
  const pollRef = useRef(null);
  const pending = job && !TERMINAL.includes(job.status);

  // ---- boot: fetch locked contract + resume any pending job ----------
  useEffect(() => {
    api("/api/preview/kling-pilot/config")
      .then((c) => setConfig(c))
      .catch((e) => setConfigErr(e.message || "config unavailable"));

    const pendingId = localStorage.getItem(PENDING_KEY);
    if (pendingId) {
      api(`/api/preview/kling-pilot/job/${pendingId}`)
        .then((doc) => {
          if (TERMINAL.includes(doc.status)) {
            localStorage.removeItem(PENDING_KEY);
            localStorage.removeItem(IDEMP_KEY);
          } else {
            setJob(doc); startPolling(pendingId);
          }
        })
        .catch(() => {
          localStorage.removeItem(PENDING_KEY);
          localStorage.removeItem(IDEMP_KEY);
        });
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- cost estimate (invalidated on every input change) --------------
  useEffect(() => { setEstimate(null); }, [durationSeconds, generateAudio, aspectRatio, prompt]);

  const refreshEstimate = useCallback(async () => {
    if (!config) return;
    try {
      const r = await api("/api/preview/kling-pilot/estimate", {
        method: "POST",
        body: { duration_seconds: durationSeconds, generate_audio: generateAudio },
      });
      setEstimate(r);
    } catch (e) { toast.error(e.message || "تعذر حساب التكلفة"); }
  }, [config, durationSeconds, generateAudio]);

  // ---- polling helpers -----------------------------------------------
  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);
  const pollOnce = useCallback(async (jobId) => {
    try {
      const doc = await api(`/api/preview/kling-pilot/job/${jobId}`);
      setJob(doc);
      if (TERMINAL.includes(doc.status)) {
        stopPolling();
        localStorage.removeItem(PENDING_KEY);
        localStorage.removeItem(IDEMP_KEY);
        if (doc.status === "ready") toast.success("اكتمل الفيديو — يمكنك التنزيل");
        else                        toast.error(doc.error_message || "فشل التوليد");
      }
    } catch (e) {
      if (e.status === 404) {
        stopPolling();
        localStorage.removeItem(PENDING_KEY);
        setJob(null);
      }
    }
  }, [stopPolling]);
  const startPolling = useCallback((jobId) => {
    stopPolling();
    pollOnce(jobId);
    pollRef.current = setInterval(() => pollOnce(jobId), POLL_MS);
  }, [pollOnce, stopPolling]);

  // ---- generate --------------------------------------------------------
  const canGenerate = !!(
    config && !config.disabled &&
    !pending &&
    prompt.trim().length >= 3 &&
    confirmPrompt && confirmCharge &&
    estimate
  );

  const handleGenerate = async () => {
    setError(null);
    let key = localStorage.getItem(IDEMP_KEY);
    if (!key) { key = uuid(); localStorage.setItem(IDEMP_KEY, key); }
    try {
      const r = await api("/api/preview/kling-pilot/generate", {
        method: "POST",
        body: {
          prompt, duration_seconds: durationSeconds,
          aspect_ratio: aspectRatio, generate_audio: generateAudio,
          confirm_prompt: confirmPrompt, confirm_charge: confirmCharge,
          idempotency_key: key,
        },
      });
      localStorage.setItem(PENDING_KEY, r.job_id);
      setJob({ job_id: r.job_id, status: r.status || "queued" });
      startPolling(r.job_id);
      toast.info("تم إرسال الطلب لـ Kling — سنبقيك على اطلاع");
    } catch (e) {
      const detail = e.body?.detail || e.body || {};
      if (e.status === 423) {
        setError(detail.message_ar || "الميزة غير مفعّلة");
      } else if (detail.message_ar) {
        setError(detail.message_ar);
      } else {
        setError(e.message || "تعذر بدء التوليد");
      }
    }
  };

  return (
    <section dir="rtl" className="mx-auto max-w-3xl px-4 py-8 space-y-6" data-testid="kling-page">
      <header className="space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <PlayCircle className="w-5 h-5 text-[#BC944A]" />
          <h1 className="text-2xl font-bold text-neutral-900" data-testid="kling-title">
            Kling 2.6 Pro — نص إلى فيديو
          </h1>
          {config?.disabled && (
            <span data-testid="kling-status-pill"
                  className="ms-auto text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-800">
              معطّل
            </span>
          )}
        </div>
        <p className="text-sm text-neutral-600">
          أداة اختبار داخلية للمشرف. Kling 2.6 Pro عبر fal.ai. الفوترة الفعلية تُحسم عند نجاح
          التوليد فقط.
        </p>
      </header>

      {configErr && (
        <div data-testid="kling-err" className="rounded-xl bg-red-50 border border-red-200 text-red-800 p-3 text-sm flex gap-2">
          <XCircle className="w-4 h-4 mt-0.5" /><span>{configErr}</span>
        </div>
      )}
      {config?.disabled && (
        <div data-testid="kling-disabled-banner"
             className="rounded-2xl border-2 border-red-300 bg-red-50 text-red-900 p-5 space-y-2">
          <div className="flex items-center gap-2 font-bold text-sm">
            <ShieldAlert className="w-5 h-5 text-red-700" />
            الميزة معطّلة حالياً (KLING_PILOT_STATUS=disabled)
          </div>
          <p className="text-sm leading-relaxed">
            التوليد المدفوع مُقفَل حتى يُفعّل المشرف الميزة صراحةً من متغيرات البيئة.
          </p>
        </div>
      )}

      {config && (
        <div data-testid="kling-config-panel"
             className="rounded-2xl border border-neutral-200 bg-neutral-50/60 p-4 text-xs text-neutral-700 space-y-1">
          <div><b>المزوّد:</b> {config.provider}</div>
          <div><b>الموديل:</b> {config.model}</div>
          <div><b>نسبة العرض:</b> {config.allowed_aspects.join("، ")}</div>
          <div><b>المدة:</b> {config.allowed_durations.join("، ")} ثوانٍ</div>
          <div>
            <b>سعر الثانية:</b> بدون صوت{" "}
            <span data-testid="kling-price-off">{config.unit_cost_sar_audio_off} SAR</span>
            {" — "}مع صوت{" "}
            <span data-testid="kling-price-on">{config.unit_cost_sar_audio_on} SAR</span>
          </div>
        </div>
      )}

      <section className="rounded-2xl border border-neutral-200 bg-white p-5 space-y-4">
        <label className="block">
          <span className="text-sm font-semibold text-neutral-800">وصف المشهد</span>
          <textarea data-testid="kling-prompt" rows={5} value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    disabled={pending || config?.disabled}
                    className="mt-1 w-full rounded-xl border border-neutral-300 p-3 text-sm
                               focus:outline-none focus:ring-2 focus:ring-[#BC944A]/40
                               disabled:bg-neutral-50 disabled:text-neutral-400"/>
        </label>

        <div className="grid grid-cols-3 gap-3">
          <label className="block">
            <span className="text-xs font-semibold text-neutral-700">نسبة العرض</span>
            <select data-testid="kling-aspect" value={aspectRatio}
                    onChange={(e) => setAspectRatio(e.target.value)}
                    disabled={pending || config?.disabled}
                    className="mt-1 w-full rounded-lg border border-neutral-300 p-2 text-sm">
              <option value="9:16">9:16 عمودي (موصى به)</option>
              <option value="16:9">16:9 أفقي</option>
              <option value="1:1">1:1 مربّع</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-neutral-700">المدة</span>
            <select data-testid="kling-duration" value={durationSeconds}
                    onChange={(e) => setDurationSeconds(parseInt(e.target.value, 10))}
                    disabled={pending || config?.disabled}
                    className="mt-1 w-full rounded-lg border border-neutral-300 p-2 text-sm">
              <option value={10}>10 ثوانٍ (افتراضي)</option>
              <option value={5}>5 ثوانٍ</option>
            </select>
          </label>
          <label className="flex items-end gap-2">
            <input data-testid="kling-audio" type="checkbox" checked={generateAudio}
                   onChange={(e) => setGenerateAudio(e.target.checked)}
                   disabled={pending || config?.disabled}
                   className="w-4 h-4"/>
            <span className="text-xs font-semibold text-neutral-700 pb-2">
              تضمين صوت (يضاعف التكلفة)
            </span>
          </label>
        </div>

        <button data-testid="kling-estimate-btn" onClick={refreshEstimate}
                disabled={!config || !prompt.trim() || pending || config?.disabled}
                className="px-4 py-2 rounded-full border border-neutral-300 text-sm text-neutral-800
                           hover:bg-neutral-100 disabled:opacity-50">
          احسب التكلفة
        </button>

        {estimate && (
          <div data-testid="kling-estimate"
               className="rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-900 p-3 text-sm">
            التكلفة المتوقعة: <b data-testid="kling-estimate-sar">{estimate.total_sar} SAR</b>
            <span className="text-emerald-700"> — لن يُحسم أي مبلغ قبل تسليم فيديو صالح.</span>
          </div>
        )}

        <div className="space-y-2">
          <label className="flex items-start gap-2 text-xs text-neutral-700">
            <input data-testid="kling-confirm-prompt" type="checkbox"
                   checked={confirmPrompt}
                   onChange={(e) => setConfirmPrompt(e.target.checked)}
                   disabled={!estimate || pending || config?.disabled}
                   className="mt-0.5 w-4 h-4"/>
            <span>راجعتُ النص وأؤكد إرساله كما هو إلى Kling.</span>
          </label>
          <label className="flex items-start gap-2 text-xs text-neutral-700">
            <input data-testid="kling-confirm-charge" type="checkbox"
                   checked={confirmCharge}
                   onChange={(e) => setConfirmCharge(e.target.checked)}
                   disabled={!estimate || pending || config?.disabled}
                   className="mt-0.5 w-4 h-4"/>
            <span>أفهم أن fal.ai قد يُحصّل من حسابي المرتبط عند نجاح التوليد فقط.</span>
          </label>
        </div>

        <button data-testid="kling-generate-btn" onClick={handleGenerate}
                disabled={!canGenerate}
                className={`w-full px-6 py-3 rounded-full text-sm font-semibold transition ${
                  canGenerate ? "bg-[#BC944A] text-white hover:bg-[#a58038]"
                              : "bg-neutral-200 text-neutral-500 cursor-not-allowed"}`}>
          {pending ? "جارٍ التوليد…" : "بدء التوليد"}
        </button>

        {error && (
          <div data-testid="kling-error" className="rounded-xl bg-red-50 border border-red-200 text-red-800 p-3 text-sm">
            {error}
          </div>
        )}
      </section>

      {job && (
        <section data-testid="kling-job-panel"
                 className="rounded-2xl border border-neutral-200 bg-white p-5 space-y-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-neutral-800">
            {pending && <Loader2 className="w-4 h-4 animate-spin text-neutral-500" />}
            <span data-testid="kling-job-status">حالة الوظيفة: {job.status}</span>
            <span className="text-[10px] text-neutral-400 ms-auto font-mono">{job.job_id}</span>
          </div>
          {pending && (
            <p className="text-xs text-neutral-600">
              نُتابع الطلب مع fal.ai. عادةً 30 ثانية إلى 3 دقائق.
            </p>
          )}
          {job.status === "ready" && job.video_url && (
            <div className="flex items-center gap-3">
              <video data-testid="kling-job-video" src={`${API_BASE}${job.video_url}`}
                     controls className="w-full max-w-md rounded-xl border" />
              <a data-testid="kling-job-download"
                 href={`${API_BASE}${job.download_url}`} download
                 className="inline-flex items-center gap-2 text-xs font-semibold text-[#BC944A] hover:underline">
                <Download className="w-4 h-4" /> تنزيل MP4
              </a>
            </div>
          )}
          {job.status === "failed" && (
            <div className="rounded-xl bg-red-50 border border-red-200 text-red-800 p-3 text-xs">
              <div className="font-semibold">تعذّر التوليد ({job.error_reason || "unknown"})</div>
              {job.error_message && <div className="mt-1">{job.error_message}</div>}
              <div className="mt-2 text-[11px] text-red-700/70">
                لم يُحسم أي مبلغ — الحجز أُعيد بالكامل.
              </div>
            </div>
          )}
        </section>
      )}
    </section>
  );
}
